import React from 'react';
import { Table, TableBody, TableCell, TableHead, TableRow, Skeleton } from '@mui/material';
import IconEdit from '@mui/icons-material/EditOutlined';
import IconDelete from '@mui/icons-material/DeleteOutlineRounded';
import IconIncome from '@mui/icons-material/NorthEastRounded';
import IconExpense from '@mui/icons-material/SouthWestRounded';
import IconRepeat from '@mui/icons-material/AutorenewRounded';
import IconReceipt from '@mui/icons-material/ReceiptLongOutlined';
import { formatCurrency, formatDate, recurrenceLabel } from '../utils/financeFormat';
import RowAction from './RowAction';
import EmptyState from './EmptyState';
import styles from './TransactionsTable.module.css';

const COLUMNS = [
    { label: 'Data' },
    { label: 'Tipo' },
    { label: 'Descrição', wide: true },
    { label: 'Categoria' },
    { label: 'Valor', right: true },
    { label: 'Recorrente' },
    { label: 'Ações', right: true }
];

function SkeletonRows() {
    return Array.from({ length: 5 }).map((_, index) => (
        <TableRow key={index}>
            {COLUMNS.map(column => (
                <TableCell key={column.label} align={column.right ? 'right' : 'left'}>
                    <Skeleton variant="text" width={column.wide ? 140 : 70} sx={{ display: 'inline-block' }} />
                </TableCell>
            ))}
        </TableRow>
    ));
}

function TypeChip({ isIncome }) {
    return (
        <span className={`${styles.typeChip} ${isIncome ? styles.chipIncome : styles.chipExpense}`}>
            {isIncome ? <IconIncome className={styles.chipIcon} /> : <IconExpense className={styles.chipIcon} />}
            {isIncome ? 'Receita' : 'Despesa'}
        </span>
    );
}

function RecurrenceCell({ transaction }) {
    const label = recurrenceLabel(transaction);
    if (!label) return <span className={styles.muted}>Não</span>;
    return (
        <span className={styles.recurrence}>
            <IconRepeat className={styles.recurrenceIcon} />
            {label}
        </span>
    );
}

/** Lista das transações do período, com editar e excluir em ícones compactos. */
function TransactionsTable({ transactions, isLoading, onEdit, onDelete, emptyAction }) {
    const showEmpty = !isLoading && transactions.length === 0;
    const count = transactions.length;

    return (
        <section className={styles.card}>
            <div className={styles.head}>
                <h3 className={styles.title}>Lançamentos</h3>
                {!isLoading && count > 0 && (
                    <span className={styles.subtitle}>
                        {count} {count === 1 ? 'transação' : 'transações'} no período
                    </span>
                )}
            </div>

            {showEmpty ? (
                <EmptyState
                    icon={<IconReceipt />}
                    title="Nenhuma transação neste período"
                    text="Troque o período ou os filtros, ou registre a primeira entrada ou saída."
                    action={emptyAction}
                />
            ) : (
                <div className={styles.scroll}>
                    <Table className={styles.table}>
                        <TableHead>
                            <TableRow>
                                {COLUMNS.map(column => (
                                    <TableCell key={column.label} align={column.right ? 'right' : 'left'}>
                                        {column.label}
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {isLoading ? <SkeletonRows /> : transactions.map((transaction, index) => {
                                const isIncome = transaction.type === 'income';
                                return (
                                    <TableRow key={`${transaction.id}-${transaction.transactionDate}-${index}`} hover>
                                        <TableCell className={styles.date}>{formatDate(transaction.transactionDate)}</TableCell>
                                        <TableCell><TypeChip isIncome={isIncome} /></TableCell>
                                        <TableCell className={styles.description}>{transaction.description}</TableCell>
                                        <TableCell>
                                            {transaction.categoryName && (
                                                <span className={styles.category}>{transaction.categoryName}</span>
                                            )}
                                        </TableCell>
                                        <TableCell
                                            align="right"
                                            className={`${styles.amount} ${isIncome ? styles.income : styles.expense}`}
                                        >
                                            {isIncome ? '+' : '-'} {formatCurrency(transaction.amount)}
                                        </TableCell>
                                        <TableCell><RecurrenceCell transaction={transaction} /></TableCell>
                                        <TableCell align="right">
                                            <div className={styles.actions}>
                                                <RowAction title="Editar" onClick={() => onEdit(transaction)}>
                                                    <IconEdit style={{ fontSize: 17 }} />
                                                </RowAction>
                                                <RowAction title="Excluir" tone="danger" onClick={() => onDelete(transaction)}>
                                                    <IconDelete style={{ fontSize: 17 }} />
                                                </RowAction>
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </div>
            )}
        </section>
    );
}

export default TransactionsTable;
